import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import axios from 'axios';
import './PreferencesPage.css';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api`;

interface Prefs {
  emailPriceUpdate:          boolean;
  emailPortfolioReport:      boolean;
  emailRlReport:             boolean;
  emailMarketMovers:         boolean;
  emailSpotlight:            boolean;
  emailDataQuality?:         boolean;
  email:                     string;
  landingPage:               string;
  defaultCollectionFormat:   string;
  defaultCollectionView:     string;
  defaultGridSize:           string;
  showAllPrintingsDefault:   boolean;
  ownedOnlyDefault:          boolean;
  defaultDeckView:           string;
}

const EMAIL_OPTIONS = [
  { key: 'emailPriceUpdate',     label: 'Weekly Price Update',     desc: 'Your collection value, gainers and losers each week',                           adminOnly: false },
  { key: 'emailPortfolioReport', label: 'Portfolio Report',        desc: 'Full portfolio breakdown by set, colour, rarity and RL status',                  adminOnly: false },
  { key: 'emailRlReport',        label: 'Reserved List Report',    desc: 'Price movements across all Reserved List cards',                                 adminOnly: false },
  { key: 'emailMarketMovers',    label: 'Market Movers',           desc: 'Top gainers and losers across all 530k cards each week',                         adminOnly: false },
  { key: 'emailSpotlight',       label: 'Daily Card Spotlight',    desc: 'AI-generated daily card spotlight with lore, art and price history',             adminOnly: false },
  { key: 'emailDataQuality',     label: 'Data Quality Report',     desc: 'Cards with missing prices, images or unmatched inventory (admin only)',          adminOnly: true  },
];

const LANDING_OPTIONS_USER = [
  { value: 'collection', label: '🃏 Collection' },
  { value: 'portfolio',  label: '📊 Portfolio'  },
  { value: 'market',     label: '📈 Market'     },
  { value: 'decks',      label: '🗂 Decks'      },
];
const LANDING_OPTIONS_ADMIN = [
  ...LANDING_OPTIONS_USER,
  { value: 'admin/jobs',     label: '⚡ Admin — Jobs'     },
  { value: 'admin/config',   label: '⚙️ Admin — Config'   },
  { value: 'admin/users',    label: '👥 Admin — Users'    },
  { value: 'admin/keywords', label: '📖 Admin — Keywords' },
];

const FORMAT_OPTIONS = [
  { value: 'standard',  label: 'Standard'  },
  { value: 'pioneer',   label: 'Pioneer'   },
  { value: 'modern',    label: 'Modern'    },
  { value: 'legacy',    label: 'Legacy'    },
  { value: 'vintage',   label: 'Vintage'   },
  { value: 'commander', label: 'Commander' },
  { value: 'pauper',    label: 'Pauper'    },
  { value: 'historic',  label: 'Historic'  },
  { value: 'explorer',  label: 'Explorer'  },
  { value: 'brawl',     label: 'Brawl'     },
  { value: '',          label: 'All Cards' },
];

export default function PreferencesPage() {
  const { user, isAdmin, refreshPreferences } = useAuth();
  const { theme, setTheme } = useTheme();
  const [prefs,   setPrefs]   = useState<Prefs | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    axios.get(`${API}/users/preferences`)
      .then(r => { setPrefs(r.data); setLoading(false); })
      .catch(() => { setError('Failed to load preferences'); setLoading(false); });
  }, []);

  const toggle = (key: string) => {
    if (!prefs) return;
    setPrefs({ ...prefs, [key]: !prefs[key as keyof Prefs] });
  };

  const set = (key: string, value: string | boolean) => {
    if (!prefs) return;
    setPrefs({ ...prefs, [key]: value });
  };

  const save = async () => {
    if (!prefs) return;
    setSaving(true); setError('');
    try {
      await axios.put(`${API}/users/preferences`, prefs);
      await refreshPreferences();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="prefs-loading">Loading preferences…</div>;

  const landingOptions = isAdmin ? LANDING_OPTIONS_ADMIN : LANDING_OPTIONS_USER;

  return (
    <div className="prefs-page">
      <div className="prefs-header">
        <h1 className="prefs-title">Preferences</h1>
        <div className="prefs-user-info">
          <span className="prefs-username">{user?.username}</span>
          <span className="prefs-email">{prefs?.email}</span>
        </div>
      </div>

      {/* ── DISPLAY ── */}
      <div className="prefs-section">
        <div className="prefs-section-title">🎨 Appearance</div>
        <div className="prefs-section-desc">Choose your preferred theme across all sessions.</div>
        <div className="prefs-theme-options">
          <div className={`prefs-theme-option ${theme === 'dark' ? 'selected' : ''}`}
               onClick={() => setTheme('dark')}>
            <div className="prefs-theme-preview prefs-theme-dark">
              <div className="prefs-theme-bar" />
              <div className="prefs-theme-cards"><div /><div /><div /></div>
            </div>
            <div className="prefs-theme-label">🌙 Dark</div>
            {theme === 'dark' && <div className="prefs-theme-check">✓</div>}
          </div>
          <div className={`prefs-theme-option ${theme === 'light' ? 'selected' : ''}`}
               onClick={() => setTheme('light')}>
            <div className="prefs-theme-preview prefs-theme-light">
              <div className="prefs-theme-bar" />
              <div className="prefs-theme-cards"><div /><div /><div /></div>
            </div>
            <div className="prefs-theme-label">☀ Light</div>
            {theme === 'light' && <div className="prefs-theme-check">✓</div>}
          </div>
        </div>
      </div>

      {/* ── NAVIGATION ── */}
      <div className="prefs-section">
        <div className="prefs-section-title">🏠 Landing Page</div>
        <div className="prefs-section-desc">Where you land after signing in.</div>
        <div className="prefs-radio-group">
          {landingOptions.map(opt => (
            <label key={opt.value} className={`prefs-radio-option ${prefs?.landingPage === opt.value ? 'selected' : ''}`}>
              <input type="radio" name="landingPage" value={opt.value}
                     checked={prefs?.landingPage === opt.value}
                     onChange={() => set('landingPage', opt.value)} />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      {/* ── COLLECTION ── */}
      <div className="prefs-section">
        <div className="prefs-section-title">🃏 Collection Browser</div>
        <div className="prefs-section-desc">Controls how the Collection page loads by default.</div>

        <div className="prefs-field">
          <label className="prefs-field-label">Default Format</label>
          <div className="prefs-field-desc">
            Filters collection to this format on load. Narrower formats load faster.
            {(['', 'legacy', 'vintage', 'historic', 'explorer'].includes(prefs?.defaultCollectionFormat || '')) && (
              <span className="prefs-perf-warn"> ⚠ Large result set — may be slow to load</span>
            )}
          </div>
          <div className="prefs-radio-group prefs-radio-inline">
            {FORMAT_OPTIONS.map(opt => (
              <label key={opt.value} className={`prefs-radio-option ${prefs?.defaultCollectionFormat === opt.value ? 'selected' : ''}`}>
                <input type="radio" name="defaultCollectionFormat" value={opt.value}
                       checked={prefs?.defaultCollectionFormat === opt.value}
                       onChange={() => set('defaultCollectionFormat', opt.value)} />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div className="prefs-field">
          <label className="prefs-field-label">Default View</label>
          <div className="prefs-radio-group prefs-radio-inline">
            <label className={`prefs-radio-option ${prefs?.defaultCollectionView === 'grid' ? 'selected' : ''}`}>
              <input type="radio" name="defaultCollectionView" value="grid"
                     checked={prefs?.defaultCollectionView === 'grid'}
                     onChange={() => set('defaultCollectionView', 'grid')} />
              🔲 Grid
            </label>
            <label className={`prefs-radio-option ${prefs?.defaultCollectionView === 'list' ? 'selected' : ''}`}>
              <input type="radio" name="defaultCollectionView" value="list"
                     checked={prefs?.defaultCollectionView === 'list'}
                     onChange={() => set('defaultCollectionView', 'list')} />
              ☰ List
            </label>
          </div>
        </div>

        <div className="prefs-field">
          <label className="prefs-field-label">Default Card Size</label>
          <div className="prefs-radio-group prefs-radio-inline">
            {(['sm','md','lg','xl'] as const).map(s => (
              <label key={s} className={`prefs-radio-option ${prefs?.defaultGridSize === s ? 'selected' : ''}`}>
                <input type="radio" name="defaultGridSize" value={s}
                       checked={prefs?.defaultGridSize === s}
                       onChange={() => set('defaultGridSize', s)} />
                {s.toUpperCase()}
              </label>
            ))}
          </div>
        </div>

        <div className="prefs-toggles">
          <div className={`prefs-option ${prefs?.ownedOnlyDefault ? 'enabled' : ''}`}
               onClick={() => toggle('ownedOnlyDefault')}>
            <div className="prefs-option-info">
              <div className="prefs-option-label">Show owned cards only by default</div>
              <div className="prefs-option-desc">Hides cards not in your collection on load</div>
            </div>
            <div className={`prefs-toggle ${prefs?.ownedOnlyDefault ? 'on' : 'off'}`}>
              <div className="prefs-toggle-knob" />
            </div>
          </div>
          <div className={`prefs-option ${prefs?.showAllPrintingsDefault ? 'enabled' : ''}`}
               onClick={() => toggle('showAllPrintingsDefault')}>
            <div className="prefs-option-info">
              <div className="prefs-option-label">Show all printings by default</div>
              <div className="prefs-option-desc">Shows every printing of each card, not just the latest</div>
            </div>
            <div className={`prefs-toggle ${prefs?.showAllPrintingsDefault ? 'on' : 'off'}`}>
              <div className="prefs-toggle-knob" />
            </div>
          </div>
        </div>
      </div>

      {/* ── DECK BUILDER ── */}
      <div className="prefs-section">
        <div className="prefs-section-title">🗂 Deck Builder</div>
        <div className="prefs-section-desc">Default view when opening the deck builder.</div>
        <div className="prefs-radio-group prefs-radio-inline">
          <label className={`prefs-radio-option ${prefs?.defaultDeckView === 'cards' ? 'selected' : ''}`}>
            <input type="radio" name="defaultDeckView" value="cards"
                   checked={prefs?.defaultDeckView === 'cards'}
                   onChange={() => set('defaultDeckView', 'cards')} />
            🔲 Card tiles
          </label>
          <label className={`prefs-radio-option ${prefs?.defaultDeckView === 'list' ? 'selected' : ''}`}>
            <input type="radio" name="defaultDeckView" value="list"
                   checked={prefs?.defaultDeckView === 'list'}
                   onChange={() => set('defaultDeckView', 'list')} />
            ☰ List
          </label>
        </div>
      </div>

      {/* ── EMAIL ── */}
      <div className="prefs-section">
        <div className="prefs-section-title">📧 Email Notifications</div>
        <div className="prefs-section-desc">
          Emails are sent to <strong>{prefs?.email}</strong>.
          Contact an admin to change your email address.
        </div>
        <div className="prefs-options">
          {EMAIL_OPTIONS.map(opt => {
            if (opt.adminOnly && !isAdmin) return null;
            const enabled = prefs?.[opt.key as keyof Prefs] as boolean ?? false;
            return (
              <div key={opt.key}
                   className={`prefs-option ${enabled ? 'enabled' : ''}`}
                   onClick={() => toggle(opt.key)}>
                <div className="prefs-option-info">
                  <div className="prefs-option-label">
                    {opt.label}
                    {opt.adminOnly && <span className="prefs-admin-badge">Admin</span>}
                  </div>
                  <div className="prefs-option-desc">{opt.desc}</div>
                </div>
                <div className={`prefs-toggle ${enabled ? 'on' : 'off'}`}>
                  <div className="prefs-toggle-knob" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {error && <div className="prefs-error">{error}</div>}

      <div className="prefs-actions">
        {saved && <span className="prefs-saved">✓ Preferences saved</span>}
        <button className="prefs-save-btn" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}
